import React, { useMemo, useState } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, useAdminGet } from '../../kit';
import { AdminLink } from '../../adminNav';
import { Drawer } from '../emailKit';
import { LIFECYCLES, LIFECYCLE_LABEL, LifecyclePill, TAGS_SECTION } from '../../tags/shared';
import { emailPeople } from '../people/shared';
import {
  Banner, Overview as OverviewData, Pill, STATE_UNKNOWN, ServerWaitBanner, StatePill, Trigger, closedGates, fmtIST, num,
  waitingForServer, windowText,
} from './shared';

/**
 * MEMAC → Overview: where members are (status × cohort, with counts) and
 * whether anything is being sent automatically. The banner says plainly
 * that nothing is sent automatically while any gate is closed — and, in
 * amber, that the state is unknown when the status could not be read.
 *
 * A matrix cell opens those accounts (tags/accounts with lifecycle + cohort;
 * the cohort filter is MEMAC's, from the raw profile score and the searches
 * count). "E-mail these" hands the ids to Campaigns — it sends nothing.
 */

const CohortAccounts: React.FC<{ status: string; cohort: string; expected: number; onClose: () => void }> =
  ({ status, cohort, expected, onClose }) => {
  const params: Record<string, string | number> = {
    ...(status ? { lifecycle: status } : {}), ...(cohort ? { cohort } : {}),
    exclude_team: '1', page: 0, per_page: 200, sort: 'created', dir: 'desc',
  };
  const res = useAdminGet<any>(TAGS_SECTION, 'accounts', params, [status, cohort]);
  const rows: any[] = res.data?.rows || [];
  const total = Number(res.data?.total ?? rows.length);
  const cols = useMemo(() => {
    const want = ['email', 'name', 'company', 'organisation', 'lifecycle', 'cohort', 'profile_raw', 'profile_pct',
      'searches', 'created_at', 'last_activity_at'];
    return rows[0] ? want.filter(k => k in rows[0]) : want.slice(0, 2);
  }, [rows]);
  const ids = rows.map(r => String(r.user_id || '')).filter(Boolean);
  return (
    <Drawer title={`${LIFECYCLE_LABEL[status] || status || 'Any status'} · cohort ${cohort || 'any'}`}
      subtitle={`${num(expected)} in the matrix · team left out`} onClose={onClose}>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading /> : (
        <>
          {cohort && total !== expected && (
            <Banner tone="warn">
              The accounts list counts {num(total)}, the matrix {num(expected)}. If the list is far bigger, the server
              does not filter accounts by cohort yet, so it shows the whole status — treat this list with care.
            </Banner>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Btn small onClick={() => emailPeople(ids)} disabled={!ids.length}
              title="Opens Campaigns → New campaign with these people picked; nothing is sent">
              E-mail these {num(ids.length)} in Campaigns…
            </Btn>
            <AdminLink to={{ group: 'tags', tab: 'accounts', ...(status ? { lifecycle: status } : {}) }}
              title="User tags → Accounts, filtered by status (the cohort is not a filter there)">
              Open the status in User tags
            </AdminLink>
            {total > rows.length && <span className="text-[11px] text-grayscale-1100">Showing the newest {num(rows.length)} of {num(total)}.</span>}
          </div>
          <DataTable rows={rows} columns={cols} csvName={`memac_${status || 'any'}_${cohort || 'any'}`}
            render={{ lifecycle: r => <LifecyclePill value={r.lifecycle} subtag={r.lifecycle_subtag} /> }} />
        </>
      )}
    </Drawer>
  );
};

const OverviewTab: React.FC<{
  ov: OverviewData | null; loading: boolean; error: string; triggers: Trigger[];
  onReload: () => void; onOpenTrigger: (id: string) => void; onOpenSettings: () => void;
}> = ({ ov, loading, error, triggers, onReload, onOpenTrigger, onOpenSettings }) => {
  const [cell, setCell] = useState<{ status: string; cohort: string; n: number } | null>(null);
  const memac = triggers.filter(t => !t.external);
  const autoOn = memac.filter(t => t.enabled && t.mode === 'auto');
  const nAuto = triggers.length ? autoOn.length : ov?.counts.auto ?? null;
  const closed = closedGates(ov, nAuto);
  const external = triggers.filter(t => t.external);

  const statuses = useMemo(() => {
    const listed = ov?.statuses.length ? ov.statuses.map(s => s.key)
      : LIFECYCLES.map(l => l.key);
    const extra = (ov?.matrix || []).map(m => m.status).filter(s => s && !listed.includes(s));
    return [...listed, ...Array.from(new Set(extra))];
  }, [ov]);
  const cohorts = ov?.cohorts || [];
  const cellN = (s: string, c: string) => ov?.matrix.find(m => m.status === s && m.cohort === c)?.count ?? 0;
  const rowTotal = (s: string) => {
    const listed = ov?.statuses.find(x => x.key === s)?.count;
    return listed ?? cohorts.reduce((a, c) => a + cellN(s, c.key), 0);
  };
  const colTotal = (c: string) => ov?.cohorts.find(x => x.key === c)?.count ?? statuses.reduce((a, s) => a + cellN(s, c), 0);
  const grand = statuses.reduce((a, s) => a + (rowTotal(s) || 0), 0);
  const max = Math.max(1, ...(ov?.matrix || []).map(m => m.count));

  if (loading && !ov) return <Loading />;
  return (
    <div className="flex flex-col gap-4">
      {error && <Msg kind="error">MEMAC status could not be read: {error}</Msg>}
      {closed === null ? (
        <Banner tone="warn">
          <b>{STATE_UNKNOWN}.</b> Whether anything is being sent automatically cannot be told until it reads —
          Refresh, or check Settings and the triggers' switches.
        </Banner>
      ) : ov && waitingForServer(ov, nAuto) ? (
        <ServerWaitBanner ov={ov} ids={autoOn.map(t => t.id)} onOpenTrigger={onOpenTrigger} />
      ) : closed.length > 0 ? (
        <Banner tone="ok">
          <b>Nothing is sent automatically</b> — {closed.join('; ')}.
          Triggers can still be dry-run, tested and sent by hand.
        </Banner>
      ) : (
        <Banner tone="warn">
          <b>Automatic sending is ON</b> for {autoOn.length} trigger{autoOn.length === 1 ? '' : 's'}:{' '}
          {autoOn.map((t, i) => <React.Fragment key={t.id}>{i > 0 && ', '}
            <button type="button" className="underline underline-offset-2" onClick={() => onOpenTrigger(t.id)}>{t.id}</button>
          </React.Fragment>)}. {ov ? `Every ${ov.settings.interval_minutes ?? '?'} min, ${windowText(ov.settings)}.` : ''}
        </Banner>
      )}

      <KPIRow>
        <KPI label="Global switch" value={!ov ? 'Unknown' : ov.settings.enabled ? 'On' : 'Off'} onClick={onOpenSettings}
          hint="Turned on and off under Settings" />
        <KPI label="Server setting" value={!ov ? 'Unknown' : ov.envAuto ? 'Set' : 'Not set'}
          hint="MEMAC_AUTO=1 on the server; applies after an API restart" />
        <KPI label="Triggers" value={num(ov?.counts.triggers ?? memac.length)}
          hint={`${num(ov?.counts.enabled ?? memac.filter(t => t.enabled).length)} on · ${num(nAuto ?? 0)} auto · ${external.length} live elsewhere`} />
        <KPI label="Scheduler" value={ov?.scheduler.running === null || ov?.scheduler.running === undefined ? '—' : ov.scheduler.running ? 'Running' : 'Stopped'}
          hint={`last ${fmtIST(ov?.scheduler.last_run_at)} · next ${fmtIST(ov?.scheduler.next_run_at)}`} />
      </KPIRow>

      <Section title="Members by status and cohort" right={<Btn small onClick={onReload}>Refresh</Btn>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Status is each account's lifecycle, worked out live. Cohort uses two signals: has the member searched
          (direct searches), and is their profile over 50% (the raw score, not the floored % tags use). Our team is
          left out. Click a number to see those accounts.
        </p>
        {!ov?.matrix.length ? (
          <div className="text-sm text-grayscale-1100 py-4 text-center">No counts yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-grayscale-1100 border-b border-grayscale-300">
                  <th className="py-2 pr-3">Status</th>
                  {cohorts.map(c => <th key={c.key} className="py-2 px-2 text-right" title={c.definition}>{c.label}</th>)}
                  <th className="py-2 pl-2 text-right">All</th>
                </tr>
              </thead>
              <tbody>
                {statuses.map(s => (
                  <tr key={s} className="border-b border-grayscale-200">
                    <td className="py-2 pr-3" title={LIFECYCLES.find(l => l.key === s)?.hint}>
                      <LifecyclePill value={s} label={ov?.statuses.find(x => x.key === s)?.label} />
                    </td>
                    {cohorts.map(c => {
                      const n = cellN(s, c.key);
                      return (
                        <td key={c.key} className="py-1 px-1 text-right">
                          <button type="button" disabled={!n} onClick={() => setCell({ status: s, cohort: c.key, n })}
                            className="w-full rounded-md px-2 py-1.5 tabular-nums text-right hover:ring-1 hover:ring-primary-400 disabled:cursor-default"
                            style={{ backgroundColor: n ? `rgba(154, 122, 76, ${0.08 + 0.42 * (n / max)})` : undefined }}
                            title={n ? `Open these ${num(n)} accounts` : 'None'}>
                            {n ? num(n) : <span className="text-grayscale-900">0</span>}
                          </button>
                        </td>
                      );
                    })}
                    <td className="py-2 pl-2 text-right font-medium tabular-nums">{num(rowTotal(s))}</td>
                  </tr>
                ))}
                <tr className="border-t border-grayscale-300">
                  <td className="py-2 pr-3 text-xs font-bold text-grayscale-1300">All statuses</td>
                  {cohorts.map(c => <td key={c.key} className="py-2 px-2 text-right font-medium tabular-nums">{num(colTotal(c.key))}</td>)}
                  <td className="py-2 pl-2 text-right font-bold tabular-nums">{num(grand)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Cohorts">
          <ul className="flex flex-col gap-2 text-sm">
            {cohorts.map(c => (
              <li key={c.key} className="flex items-start gap-3">
                <span className="w-7 h-7 shrink-0 rounded-full bg-primary-100 border border-primary-300 flex items-center justify-center font-bold font-grotesk">{c.key}</span>
                <span className="flex-1">
                  <b>{c.label.replace(/^[A-D]\s*[—-]\s*/, '')}</b>
                  <span className="block text-xs text-grayscale-1100">{c.definition}</span>
                </span>
                {c.count !== null && <span className="tabular-nums font-medium">{num(c.count)}</span>}
              </li>
            ))}
          </ul>
        </Section>
        <Section title="Statuses">
          <ul className="flex flex-col gap-1.5 text-sm">
            {statuses.map(s => (
              <li key={s} className="flex items-center gap-2">
                <LifecyclePill value={s} />
                <span className="text-xs text-grayscale-1100 flex-1">{LIFECYCLES.find(l => l.key === s)?.hint || ''}</span>
                <span className="tabular-nums font-medium">{num(rowTotal(s))}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-grayscale-1100">
            Explorer also holds members whose paid plan ran out; a never-signed-in assigned Voyager stays Assigned.
          </p>
        </Section>
      </div>

      <Section title="Live mails MEMAC does not send">
        <p className="text-xs text-grayscale-1100 -mt-1">
          These already go out automatically from existing code. MEMAC lists them with their real switch and never
          sends them, so switching a MEMAC trigger on can never double-send them.
        </p>
        {!external.length ? <div className="text-sm text-grayscale-1100">None listed by the server.</div> : (
          <div className="flex flex-col divide-y divide-grayscale-200">
            {external.map(t => (
              <div key={t.id} className="py-2 flex items-start gap-3 flex-wrap text-sm">
                <button type="button" onClick={() => onOpenTrigger(t.id)} className="font-mono text-xs text-primary-700 hover:underline w-14 pt-0.5">{t.id}</button>
                <div className="flex-1 min-w-[14rem]">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-[11px] text-grayscale-1100">
                    {t.templateKeys.join(', ') || '—'} · switch: {t.ext?.where}
                    {t.ext?.link && <> · <AdminLink to={t.ext.link}>open</AdminLink></>}
                  </div>
                </div>
                <StatePill t={t} />
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Current defaults (all can be changed)">
        <ul className="list-disc pl-5 text-xs text-grayscale-1200 flex flex-col gap-1">
          <li>The status cap ({ov?.settings.status_cap_30d ?? 6} non-service mails per member per rolling 30 days) counts MEMAC mails and campaigns.</li>
          <li>Renewal reminder VP-05 at 30, 7 and 1 days before the plan ends (MK-5 lists 14 and 3 as the alternative).</li>
          <li>UA-1 (profile incomplete, day 3) and EX-03 (day 5) are both seeded and overlap — switch on one.</li>
          <li>TL-01 is its own trigger; the live trial-lapsed mail (TA-03) stays as it is.</li>
          <li>Explorers can search today — MEMAC copy says filters and full contact details are Voyager, not search.</li>
          <li>IN-1 (industry news) is on hold and cannot be switched on. Razorpay's own mails are listed, never sent.</li>
          <li>Every MEMAC mail carries a per-category unsubscribe link; unsubscribed members are left out of non-service triggers.</li>
        </ul>
        <div className="flex gap-1 flex-wrap">
          <Pill tone="mute">Week = Monday, IST</Pill><Pill tone="mute">Team left out</Pill><Pill tone="mute">Seeded Off + Manual</Pill>
        </div>
      </Section>

      {cell && <CohortAccounts status={cell.status} cohort={cell.cohort} expected={cell.n} onClose={() => setCell(null)} />}
    </div>
  );
};

export default OverviewTab;
